import { formatDate } from '@portal/ui';
import { apiFetch } from '@portal/web-sdk';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationList, NotificationPush, UnreadCount } from '../contracts.js';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unread: boolean) => ['notifications', 'list', unread] as const,
  unread: ['notifications', 'unread-count'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

export const useUnreadCount = () =>
  useQuery({
    queryKey: notificationKeys.unread,
    queryFn: () => apiFetch<UnreadCount>('notifications/unread-count'),
    // The stream pushes changes; this only catches up after a lost connection.
    refetchInterval: 5 * 60_000,
  });

export const useNotifications = (unread: boolean, limit = 30) =>
  useInfiniteQuery({
    queryKey: [...notificationKeys.list(unread), limit],
    queryFn: ({ pageParam }) =>
      apiFetch<NotificationList>(
        `notifications?${new URLSearchParams({
          limit: String(limit),
          ...(unread ? { unread: 'true' } : {}),
          ...(pageParam ? { before: pageParam } : {}),
        })}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/**
 * Listens to the server's notification stream (SSE) while the app is open: refreshes the inbox and
 * calls `onNotification` for each new one. EventSource reconnects by itself.
 */
export function useNotificationStream(onNotification: (n: NotificationPush) => void): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const source = new EventSource('/api/v1/notifications/stream', { withCredentials: true });
    const handle = (event: MessageEvent<string>) => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      try {
        onNotification(JSON.parse(event.data) as NotificationPush);
      } catch {
        // Ignore malformed events.
      }
    };
    source.addEventListener('notification', handle);
    return () => {
      source.removeEventListener('notification', handle);
      source.close();
    };
  }, [queryClient, onNotification]);
}

export function useRelativeTime(): (iso: string) => string {
  const { i18n } = useTranslation();
  return (iso) => {
    const seconds = Math.round((Date.parse(iso) - Date.now()) / 1000);
    const format = new Intl.RelativeTimeFormat(i18n.language.startsWith('vi') ? 'vi' : 'en', {
      numeric: 'auto',
    });
    const abs = Math.abs(seconds);
    if (abs < 60) return format.format(seconds, 'second');
    if (abs < 3600) return format.format(Math.round(seconds / 60), 'minute');
    if (abs < 86_400) return format.format(Math.round(seconds / 3600), 'hour');
    if (abs < 7 * 86_400) return format.format(Math.round(seconds / 86_400), 'day');
    return formatDate(iso);
  };
}
